import { useState } from "react";
import {
  Table,
  Dropdown,
  Menu,
  Modal,
  Select,
  Space,
  message,
  Form,
  InputNumber,
  Input,
  Empty,
  Collapse,
  Image,
} from "antd";
import {
  AppstoreAddOutlined,
  DeleteOutlined,
  EditOutlined,
  FileAddOutlined,
  MoreOutlined,
  RetweetOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { getOutlets } from "../actions/acc_actions";
import { useSelector } from "react-redux";
import {
  addMenutoOutlet,
  createMenuItem,
  createMenuSection,
  deleteMenu,
  updateMenuItem,
  updateMenuSection,
} from "../actions/merchant_actions";
import MenuItemForm from "./MenuItemFom";
import MenuFormModal from "./MenuFormModal";
import MenuImageUploader from "./MenuImageUploader";
import { useTranslation } from "react-i18next";

const { Panel } = Collapse;

const MenuTable = ({ data, refetchMenus }) => {
  const { t } = useTranslation();
  const businessUrn = useSelector((state) => state.account.businessUrn);
  const accUrn = useSelector((state) => state.account.urn);
  const [menuSectionForm] = Form.useForm();
  const safeData = Array.isArray(data) ? data : [];

  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [sectionModalVisible, setSectionModalVisible] = useState(false);
  const [selectedMenuUrn, setSelectedMenuUrn] = useState(null);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [isMenuModalVisible, setIsMenuModalVisible] = useState(false);
  const [editingSection, setEditingSection] = useState(null);

  const { isLoading, data: outletsData } = useQuery({
    queryKey: ["outlets", businessUrn],
    queryFn: () => getOutlets(businessUrn),
    enabled: !!businessUrn,
  });

  const handleEditMenu = (record) => {
    setSelectedMenu(record);
    setIsMenuModalVisible(true);
  };

  const handleAddItem = async (item) => {
    const data = { ...item };
    try {
      if (selectedMenuItem?.urn) {
        await updateMenuItem({
          account_urn: accUrn,
          itemUrn: selectedMenuItem.urn,
          data,
        });
        message.success(t("menu_item_updated"));
      } else {
        await createMenuItem({ account_urn: accUrn, data });
        message.success(t("menu_item_created"));
      }
      refetchMenus();
      setItemModalVisible(false);
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          t("something_went_wrong")
      );
    }
  };

  const handleAddToOutlet = (record) => {
    setSelectedMenuUrn(record.urn);
    setModalVisible(true);
  };

  const handleCreateSection = (record) => {
    setSelectedMenuUrn(record.urn);
    setSectionModalVisible(true);
  };

  const onCreateSection = async () => {
    try {
      const values = await menuSectionForm.validateFields();
      if (editingSection) {
        await updateMenuSection({
          account_urn: accUrn,
          menuUrn: selectedMenuUrn,
          data: values,
        });
        message.success(t("section_updated"));
      } else {
        await createMenuSection({
          account_urn: accUrn,
          data: values,
          menuUrn: selectedMenuUrn,
        });
        message.success(t("section_created"));
      }
      refetchMenus();
      setSectionModalVisible(false);
      setSelectedMenuUrn(null);
      menuSectionForm.resetFields();
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          t("something_went_wrong")
      );
    }
  };

  const handleAssignMenu = async () => {
    if (!selectedOutlet) {
      message.warning(t("select_outlet_warning"));
      return;
    }
    try {
      await addMenutoOutlet({
        account_urn: accUrn,
        outletUrn: selectedOutlet,
        menuUrn: selectedMenuUrn,
      });
      refetchMenus();
      message.success(t("menu_assigned_outlet"));
      setModalVisible(false);
      setSelectedMenuUrn(null);
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          t("something_went_wrong")
      );
    }
    setSelectedOutlet(null);
  };

  const handleDeleteMenu = async (record) => {
    try {
      await deleteMenu({ account_urn: accUrn, menuUrn: record.urn });
      refetchMenus();
      message.success(t("menu_deleted"));
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          t("something_went_wrong")
      );
    }
    setSelectedOutlet(null);
  };

  const menuColumns = ({ handleAddToOutlet, handleCreateSection }) => [
    { title: t("menu_name"), dataIndex: ["data", "name"], key: "name" },
    {
      title: t("currency"),
      dataIndex: ["data", "currency_code"],
      key: "currency",
    },
    {
      title: t("min_payment"),
      dataIndex: ["data", "minimum_payment_amount"],
      key: "minPayment",
    },
    {
      title: t("max_payment"),
      dataIndex: ["data", "maximum_payment_amount"],
      key: "maxPayment",
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item
              icon={<RetweetOutlined />}
              key="assign"
              onClick={() => handleAddToOutlet(record)}
            >
              {t("assign_to_outlet")}
            </Menu.Item>
            <Menu.Item
              key="2"
              icon={<FileAddOutlined />}
              onClick={() => handleCreateSection(record)}
            >
              {t("create_section")}
            </Menu.Item>
            <Menu.Item
              icon={<EditOutlined />}
              key="edit"
              onClick={() => handleEditMenu(record)}
            >
              {t("edit")}
            </Menu.Item>
            <Menu.Item
              icon={<DeleteOutlined />}
              danger
              key="3"
              onClick={() => {
                Modal.confirm({
                  title: t("confirm_delete_menu"),
                  okText: t("yes_delete"),
                  okType: "danger",
                  cancelText: t("cancel"),
                  onOk() {
                    handleDeleteMenu(record);
                  },
                });
              }}
            >
              {t("delete_menu")}
            </Menu.Item>
          </Menu>
        );
        return (
          <Dropdown overlay={menu}>
            <MoreOutlined />
          </Dropdown>
        );
      },
    },
  ];

  const productColumns = [
    {
      title: t("image"),
      dataIndex: "imagePath",
      key: "image",
      render: (_, record) => (
        <Image
          src={record?.imagePath || "/assets/images/bg/placeholder.jpg"}
          width={80}
          height={80}
          style={{ objectFit: "cover", borderRadius: 4 }}
          fallback="/assets/images/bg/placeholder.jpg"
        />
      ),
    },
    { title: t("sku"), dataIndex: "sku", key: "sku" },
    { title: t("name"), dataIndex: "name", key: "name" },
    { title: t("tax_percent"), dataIndex: "tax_percent", key: "tax" },
    { title: t("price_incl_tax"), dataIndex: "price_inc_tax", key: "price" },
    {
      key: "settings",
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "1",
                label: t("edit"),
                icon: <EditOutlined />,
                onClick: () => {
                  setSelectedMenuItem(record);
                  setItemModalVisible(true);
                },
              },
            ],
          }}
        >
          <a onClick={(e) => e.preventDefault()}>
            <MoreOutlined />
          </a>
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <Table
        style={{ marginTop: 20 }}
        columns={menuColumns({ handleAddToOutlet, handleCreateSection })}
        dataSource={safeData.filter((menu) => !menu.deleted_at)}
        rowKey="urn"
        expandable={{
          expandedRowRender: (record) => {
            const sections = (record?.data?.sections || []).sort(
              (a, b) => a.weight - b.weight
            );
            const allProducts = record?.data?.products || [];

            return (
              <Collapse size="small" accordion>
                {sections.map((section, idx) => {
                  const sectionProducts = (section.products || [])
                    .map((p) => {
                      const product = allProducts.find(
                        (prod) => prod.sku === p.sku
                      );
                      return product ? { ...product, weight: p.weight } : null;
                    })
                    .filter(Boolean);

                  return (
                    <Panel
                      header={
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div>
                            <Image
                              src={
                                section.imagePath ||
                                "/assets/images/bg/placeholder.jpg"
                              }
                              width={50}
                              height={50}
                              style={{ objectFit: "cover", borderRadius: 4 }}
                              preview={false}
                              fallback="/assets/images/bg/placeholder.jpg"
                            />
                            <strong style={{ marginLeft: 10 }}>
                              {section.title}
                            </strong>
                            {section.description && (
                              <div style={{ fontSize: 12, color: "#999" }}>
                                {section.description}
                              </div>
                            )}
                          </div>

                          <Dropdown
                            menu={{
                              items: [
                                {
                                  key: "1",
                                  label: t("new_item"),
                                  icon: <AppstoreAddOutlined />,
                                  onClick: () => {
                                    setSelectedMenuUrn(record?.urn);
                                    setSelectedSection(section);
                                    setItemModalVisible(true);
                                  },
                                },
                                {
                                  key: "2",
                                  label: t("edit"),
                                  icon: <EditOutlined />,
                                  onClick: () => {
                                    setEditingSection(section);
                                    menuSectionForm.setFieldsValue(section);
                                    setSelectedMenuUrn(record?.urn);
                                    setSectionModalVisible(true);
                                  },
                                },
                                {
                                  key: "3",
                                  icon: <DeleteOutlined />,
                                  danger: true,
                                  label: t("delete"),
                                  onClick: () => {
                                    Modal.confirm({
                                      title: t("confirm_delete_menu"),
                                      okText: t("yes_delete"),
                                      okType: "danger",
                                      cancelText: t("cancel"),
                                      onOk() {
                                        handleDeleteMenu(record);
                                      },
                                    });
                                  },
                                },
                              ],
                            }}
                          >
                            <a onClick={(e) => e.preventDefault()}>
                              <MoreOutlined />
                            </a>
                          </Dropdown>
                        </div>
                      }
                      key={idx}
                    >
                      {sectionProducts.length > 0 ? (
                        <Table
                          columns={productColumns}
                          dataSource={sectionProducts}
                          pagination={false}
                          rowKey="sku"
                          size="small"
                        />
                      ) : (
                        <Empty
                          imageStyle={{ height: 60 }}
                          description={t("no_items")}
                        />
                      )}
                    </Panel>
                  );
                })}
              </Collapse>
            );
          },
          rowExpandable: (record) =>
            Array.isArray(record?.data?.sections) &&
            record.data.sections.length > 0,
        }}
      />

      <Modal
        title={t("assign_menu_to_outlet")}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleAssignMenu}
        okText={t("confirm")}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Select
            loading={isLoading}
            placeholder={t("select_outlet")}
            onChange={(value) => setSelectedOutlet(value)}
            style={{ width: "100%" }}
          >
            {outletsData?.data?.body.map((outlet) => (
              <Select.Option key={outlet.urn} value={outlet.urn}>
                {outlet.name}
              </Select.Option>
            ))}
          </Select>
        </Space>
      </Modal>

      <Modal
        title={t("create_section")}
        open={sectionModalVisible}
        onCancel={() => setSectionModalVisible(false)}
        onOk={onCreateSection}
        okText={t("confirm")}
      >
        <Form form={menuSectionForm} layout="vertical" name="menuSectionForm">
          <Form.Item name="imageId" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="imagePath" hidden>
            <Input type="hidden" />
          </Form.Item>

          <Form.Item label={t("image")}>
            <MenuImageUploader form={menuSectionForm} />
          </Form.Item>

          <Form.Item
            name="title"
            label={t("title")}
            rules={[
              { required: true, message: t("please_enter_section_title") },
            ]}
          >
            <Input placeholder={t("section_title")} />
          </Form.Item>

          <Form.Item
            name="description"
            label={t("description")}
            rules={[{ required: true, message: t("please_enter_description") }]}
          >
            <Input.TextArea rows={3} placeholder={t("section_description")} />
          </Form.Item>

          <Form.Item
            name="weight"
            label={t("weight")}
            rules={[
              { required: true, message: t("please_enter_section_weight") },
            ]}
          >
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
        </Form>
      </Modal>

      <MenuItemForm
        visible={itemModalVisible}
        onClose={() => {
          setItemModalVisible(false);
          setSelectedMenuItem(null);
        }}
        onSubmit={handleAddItem}
        initialValues={selectedMenuItem}
      />

      <MenuFormModal
        visible={isMenuModalVisible}
        setVisible={setIsMenuModalVisible}
        initialValues={selectedMenu}
        refetchMenus={refetchMenus}
      />
    </>
  );
};

export default MenuTable;
